// ─── Mob types ──────────────────────────────────────────────────────
export const enum MobType { Sheep = 0, Chicken = 1, Villager = 2, Horse = 3, Wolf = 4 }

export const enum AnimalState { Idle = 0, Wander = 1, Eat = 2, Flee = 3, Hunt = 4 }

export const enum VillagerJob {
  Farmer = 0, Blacksmith = 1, Merchant = 2, Guard = 3, Fisherman = 4, Priest = 5,
  Shepherd = 6, Miner = 7, Lumberjack = 8,
}

export const enum VillagerState {
  Sleep = 0, Idle = 1, Walk = 2, Working = 3, Socializing = 4, Patrol = 5,
}

// ─── Per-mob data ───────────────────────────────────────────────────
export interface MobData {
  type: MobType
  subType: number         // VillagerJob for villagers, color variant for animals
  villageIndex: number

  // Sphere-surface position (unit normal)
  nx: number; ny: number; nz: number
  radius: number          // cached sampleTerrainRadius result

  // Home position (spawn point)
  homeNx: number; homeNy: number; homeNz: number

  // Heading in tangent plane (radians)
  heading: number
  speed: number

  // AI
  state: number
  stateTimer: number

  // Wander
  wanderAngle: number

  // Villager: pathfinding target (normal direction on sphere)
  targetNx: number; targetNy: number; targetNz: number
  hasTarget: boolean

  // Animation
  animPhase: number
  animSpeed: number       // 0 = idle, 1 = walking, >1 = running

  // Mount state (horses)
  mounted: boolean
}

// ─── Speed constants ────────────────────────────────────────────────
export const MOB_SPEEDS: Record<number, { wander: number; fast: number }> = {
  [MobType.Sheep]:    { wander: 1.5, fast: 6 },
  [MobType.Chicken]:  { wander: 2.0, fast: 8 },
  [MobType.Villager]: { wander: 2.5, fast: 4 },
  [MobType.Horse]:    { wander: 3.0, fast: 10 },
  [MobType.Wolf]:     { wander: 3.5, fast: 12 },
}

// ─── Home range (angular radians from home) ─────────────────────────
export const MOB_HOME_RANGE: Record<number, number> = {
  [MobType.Sheep]:    0.0012,  // ~12 units (stay within pasture fence)
  [MobType.Chicken]:  0.0015,  // ~15 units
  [MobType.Villager]: 0.008,   // ~80 units (needs to reach buildings)
  [MobType.Horse]:    0.006,   // ~60 units (roam meadows)
  [MobType.Wolf]:     0.008,   // ~80 units (patrol territory)
}

// ─── Flee distance (angular radians) ────────────────────────────────
export const FLEE_TRIGGER_DIST = 0.004  // ~40 units
export const FLEE_DURATION = 3          // seconds

// ─── Wolf hunt parameters ───────────────────────────────────────────
export const WOLF_HUNT_TRIGGER = 0.008   // ~80 units detect range
export const WOLF_HUNT_GIVE_UP = 0.015   // ~150 units give up range
export const WOLF_HUNT_SPEED = 10        // units/s when hunting

// ─── Villager schedules ─────────────────────────────────────────────
export interface ScheduleEntry {
  hour: number
  state: VillagerState
  targetStructure?: string   // go to this building type
}

export const VILLAGER_SCHEDULES: Record<number, ScheduleEntry[]> = {
  [VillagerJob.Farmer]: [
    { hour: 5,   state: VillagerState.Walk,        targetStructure: 'farm' },
    { hour: 5.5, state: VillagerState.Working },
    { hour: 10,  state: VillagerState.Walk,        targetStructure: 'trading_post' },
    { hour: 10.5,state: VillagerState.Idle },       // depositing goods
    { hour: 11,  state: VillagerState.Walk,        targetStructure: 'pub' },
    { hour: 11.5,state: VillagerState.Socializing },
    { hour: 13,  state: VillagerState.Walk,        targetStructure: 'farm' },
    { hour: 13.5,state: VillagerState.Working },
    { hour: 17,  state: VillagerState.Walk,        targetStructure: 'trading_post' },
    { hour: 17.5,state: VillagerState.Idle },       // depositing goods
    { hour: 18,  state: VillagerState.Walk,        targetStructure: 'pub' },
    { hour: 18.5,state: VillagerState.Socializing },
    { hour: 21,  state: VillagerState.Walk,        targetStructure: 'house' },
    { hour: 21.5,state: VillagerState.Sleep },
  ],
  [VillagerJob.Blacksmith]: [
    { hour: 6,   state: VillagerState.Walk,        targetStructure: 'armory' },
    { hour: 6.5, state: VillagerState.Working },
    { hour: 11,  state: VillagerState.Walk,        targetStructure: 'trading_post' },
    { hour: 11.5,state: VillagerState.Idle },       // selling crafted goods
    { hour: 12,  state: VillagerState.Walk,        targetStructure: 'pub' },
    { hour: 12.5,state: VillagerState.Socializing },
    { hour: 13.5,state: VillagerState.Walk,        targetStructure: 'armory' },
    { hour: 14,  state: VillagerState.Working },
    { hour: 18,  state: VillagerState.Walk,        targetStructure: 'trading_post' },
    { hour: 18.5,state: VillagerState.Idle },       // selling crafted goods
    { hour: 19,  state: VillagerState.Walk,        targetStructure: 'pub' },
    { hour: 19.5,state: VillagerState.Socializing },
    { hour: 22,  state: VillagerState.Walk,        targetStructure: 'house' },
    { hour: 22.5,state: VillagerState.Sleep },
  ],
  [VillagerJob.Merchant]: [
    { hour: 7,   state: VillagerState.Walk,        targetStructure: 'trading_post' },
    { hour: 7.5, state: VillagerState.Working },
    { hour: 12,  state: VillagerState.Walk,        targetStructure: 'pub' },
    { hour: 12.5,state: VillagerState.Socializing },
    { hour: 13.5,state: VillagerState.Walk,        targetStructure: 'trading_post' },
    { hour: 14,  state: VillagerState.Working },
    { hour: 18,  state: VillagerState.Idle },
    { hour: 19,  state: VillagerState.Walk,        targetStructure: 'pub' },
    { hour: 19.5,state: VillagerState.Socializing },
    { hour: 21,  state: VillagerState.Walk,        targetStructure: 'house' },
    { hour: 21.5,state: VillagerState.Sleep },
  ],
  [VillagerJob.Guard]: [
    { hour: 5,   state: VillagerState.Walk,        targetStructure: 'castle' },
    { hour: 5.5, state: VillagerState.Patrol },
    { hour: 12,  state: VillagerState.Walk,        targetStructure: 'pub' },
    { hour: 12.5,state: VillagerState.Socializing },
    { hour: 13.5,state: VillagerState.Patrol },
    { hour: 20,  state: VillagerState.Walk,        targetStructure: 'pub' },
    { hour: 20.5,state: VillagerState.Socializing },
    { hour: 22,  state: VillagerState.Walk,        targetStructure: 'house' },
    { hour: 22.5,state: VillagerState.Sleep },
  ],
  [VillagerJob.Fisherman]: [
    { hour: 4,   state: VillagerState.Walk,        targetStructure: 'dock' },
    { hour: 4.5, state: VillagerState.Working },
    { hour: 10,  state: VillagerState.Walk,        targetStructure: 'trading_post' },
    { hour: 10.5,state: VillagerState.Idle },       // selling catch
    { hour: 11,  state: VillagerState.Walk,        targetStructure: 'pub' },
    { hour: 11.5,state: VillagerState.Socializing },
    { hour: 13,  state: VillagerState.Walk,        targetStructure: 'dock' },
    { hour: 13.5,state: VillagerState.Working },
    { hour: 17,  state: VillagerState.Walk,        targetStructure: 'trading_post' },
    { hour: 17.5,state: VillagerState.Idle },       // selling catch
    { hour: 18,  state: VillagerState.Walk,        targetStructure: 'house' },
    { hour: 18.5,state: VillagerState.Idle },
    { hour: 20,  state: VillagerState.Sleep },
  ],
  [VillagerJob.Priest]: [
    { hour: 5,   state: VillagerState.Walk,        targetStructure: 'church' },
    { hour: 5.5, state: VillagerState.Working },
    { hour: 10,  state: VillagerState.Idle },
    { hour: 12,  state: VillagerState.Walk,        targetStructure: 'pub' },
    { hour: 12.5,state: VillagerState.Socializing },
    { hour: 14,  state: VillagerState.Walk,        targetStructure: 'church' },
    { hour: 14.5,state: VillagerState.Working },
    { hour: 18,  state: VillagerState.Idle },
    { hour: 20,  state: VillagerState.Walk,        targetStructure: 'house' },
    { hour: 20.5,state: VillagerState.Sleep },
  ],
  [VillagerJob.Shepherd]: [
    { hour: 5,   state: VillagerState.Walk,        targetStructure: 'pasture' },
    { hour: 5.5, state: VillagerState.Working },
    { hour: 10,  state: VillagerState.Walk,        targetStructure: 'trading_post' },
    { hour: 10.5,state: VillagerState.Idle },       // depositing wool/animals
    { hour: 11,  state: VillagerState.Walk,        targetStructure: 'pub' },
    { hour: 11.5,state: VillagerState.Socializing },
    { hour: 13,  state: VillagerState.Walk,        targetStructure: 'pasture' },
    { hour: 13.5,state: VillagerState.Working },
    { hour: 17,  state: VillagerState.Walk,        targetStructure: 'trading_post' },
    { hour: 17.5,state: VillagerState.Idle },       // depositing wool/animals
    { hour: 18,  state: VillagerState.Walk,        targetStructure: 'pub' },
    { hour: 18.5,state: VillagerState.Socializing },
    { hour: 21,  state: VillagerState.Walk,        targetStructure: 'house' },
    { hour: 21.5,state: VillagerState.Sleep },
  ],
  [VillagerJob.Miner]: [
    { hour: 5,   state: VillagerState.Walk,        targetStructure: 'barn' },
    { hour: 5.5, state: VillagerState.Working },    // mining (use barn as mine proxy)
    { hour: 11,  state: VillagerState.Walk,        targetStructure: 'trading_post' },
    { hour: 11.5,state: VillagerState.Idle },       // depositing ore
    { hour: 12,  state: VillagerState.Walk,        targetStructure: 'pub' },
    { hour: 12.5,state: VillagerState.Socializing },
    { hour: 14,  state: VillagerState.Walk,        targetStructure: 'barn' },
    { hour: 14.5,state: VillagerState.Working },
    { hour: 18,  state: VillagerState.Walk,        targetStructure: 'trading_post' },
    { hour: 18.5,state: VillagerState.Idle },       // depositing ore
    { hour: 19,  state: VillagerState.Walk,        targetStructure: 'pub' },
    { hour: 19.5,state: VillagerState.Socializing },
    { hour: 21.5,state: VillagerState.Walk,        targetStructure: 'house' },
    { hour: 22,  state: VillagerState.Sleep },
  ],
  [VillagerJob.Lumberjack]: [
    { hour: 5,   state: VillagerState.Walk,        targetStructure: 'barn' },
    { hour: 5.5, state: VillagerState.Working },    // chopping (use barn as work proxy)
    { hour: 10,  state: VillagerState.Walk,        targetStructure: 'trading_post' },
    { hour: 10.5,state: VillagerState.Idle },       // depositing wood
    { hour: 11,  state: VillagerState.Walk,        targetStructure: 'pub' },
    { hour: 11.5,state: VillagerState.Socializing },
    { hour: 13,  state: VillagerState.Walk,        targetStructure: 'barn' },
    { hour: 13.5,state: VillagerState.Working },
    { hour: 17,  state: VillagerState.Walk,        targetStructure: 'trading_post' },
    { hour: 17.5,state: VillagerState.Idle },       // depositing wood
    { hour: 18,  state: VillagerState.Walk,        targetStructure: 'pub' },
    { hour: 18.5,state: VillagerState.Socializing },
    { hour: 21,  state: VillagerState.Walk,        targetStructure: 'house' },
    { hour: 21.5,state: VillagerState.Sleep },
  ],
}

// ─── Villager color palettes ────────────────────────────────────────
export const VILLAGER_COLORS: Record<number, {
  tunic: [number, number, number]
  pants: [number, number, number]
  skin: [number, number, number]
  hair: [number, number, number]
}> = {
  [VillagerJob.Farmer]:     { tunic: [0.55, 0.42, 0.28], pants: [0.35, 0.30, 0.22], skin: [0.85, 0.70, 0.55], hair: [0.30, 0.22, 0.14] },
  [VillagerJob.Blacksmith]: { tunic: [0.28, 0.26, 0.30], pants: [0.22, 0.20, 0.18], skin: [0.80, 0.65, 0.50], hair: [0.15, 0.12, 0.10] },
  [VillagerJob.Merchant]:   { tunic: [0.58, 0.22, 0.16], pants: [0.32, 0.26, 0.20], skin: [0.88, 0.72, 0.58], hair: [0.35, 0.25, 0.15] },
  [VillagerJob.Guard]:      { tunic: [0.38, 0.42, 0.52], pants: [0.28, 0.28, 0.32], skin: [0.82, 0.68, 0.52], hair: [0.20, 0.16, 0.12] },
  [VillagerJob.Fisherman]:  { tunic: [0.42, 0.52, 0.56], pants: [0.30, 0.35, 0.32], skin: [0.86, 0.72, 0.56], hair: [0.40, 0.30, 0.18] },
  [VillagerJob.Priest]:     { tunic: [0.78, 0.72, 0.62], pants: [0.65, 0.60, 0.50], skin: [0.84, 0.70, 0.54], hair: [0.50, 0.45, 0.38] },
  [VillagerJob.Shepherd]:   { tunic: [0.48, 0.50, 0.35], pants: [0.38, 0.36, 0.28], skin: [0.86, 0.72, 0.56], hair: [0.42, 0.32, 0.20] },
  [VillagerJob.Miner]:      { tunic: [0.40, 0.35, 0.30], pants: [0.25, 0.22, 0.20], skin: [0.78, 0.62, 0.48], hair: [0.18, 0.14, 0.10] },
  [VillagerJob.Lumberjack]: { tunic: [0.50, 0.38, 0.22], pants: [0.32, 0.28, 0.20], skin: [0.84, 0.68, 0.52], hair: [0.35, 0.28, 0.16] },
}

// ─── Village data for simulation ────────────────────────────────────
export interface VillageSimData {
  normal: { x: number; y: number; z: number }
  position: { x: number; y: number; z: number }
  tangentX: { x: number; y: number; z: number }
  tangentZ: { x: number; y: number; z: number }
  structures: Array<{ type: string; localX: number; localZ: number; rotation: number }>
  roads: Array<{ fromX: number; fromZ: number; toX: number; toZ: number }>
}

// Body part IDs for vertex shader animation
export const BODY = 0
export const LEG_BL = 1
export const LEG_BR = 2
export const LEG_FL = 3
export const LEG_FR = 4
export const HEAD = 5
export const TAIL = 6
export const ARM_L = 7
export const ARM_R = 8
